import type { BufferedReaction } from '../../shared/bufferedReaction';
import type { BufferedSample } from '../../shared/bufferedSample';

const KEY_PREFIX = 'whatlanded:pending:';

type PendingSession = {
  test_id: string;
  reactions: BufferedReaction[];
  samples: BufferedSample[];
};

function readSession(reviewerId: string): PendingSession | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + reviewerId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSession>;
    if (typeof parsed?.test_id !== 'string') return null;
    return {
      test_id: parsed.test_id,
      reactions: Array.isArray(parsed.reactions) ? parsed.reactions : [],
      samples: Array.isArray(parsed.samples) ? parsed.samples : []
    };
  } catch {
    return null;
  }
}

// The localStorage mirror behind spec §4's "a dropped connection or a
// backgrounded tab does not lose the session".
//
// Every unflushed event is written here as well as held in memory, so the data
// survives the two failure modes that actually happen on a phone: iOS discarding
// a backgrounded tab, and a reviewer walking out of wifi range mid-video. It is
// cleared only once the server has acknowledged the events.
//
// Every method swallows its own errors. Safari in private mode throws on
// localStorage.setItem when the quota is zero, and a reviewer losing their whole
// session to a storage exception would be a far worse outcome than losing the
// crash-safety net.
export const pendingBatchStorage = {
  write(reviewerId: string, session: PendingSession): void {
    try {
      window.localStorage.setItem(KEY_PREFIX + reviewerId, JSON.stringify(session));
    } catch {
      // Ignore: in-memory buffer is still authoritative for this tab.
    }
  },

  read(reviewerId: string): PendingSession | null {
    return readSession(reviewerId);
  },

  clear(reviewerId: string): void {
    try {
      window.localStorage.removeItem(KEY_PREFIX + reviewerId);
    } catch {
      // Ignore.
    }
  },

  // Reviewer ids of sessions that still have unsent events, including sessions
  // from previous page loads. Used on mount to drain orphans.
  listReviewerIds(): string[] {
    const ids: string[] = [];
    try {
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (key?.startsWith(KEY_PREFIX)) ids.push(key.slice(KEY_PREFIX.length));
      }
    } catch {
      // Ignore.
    }
    return ids;
  }
};
